import {
  CompanySalesBranchLabel,
  CompanySalesGroupBy,
  CompanySalesReportParams,
  CompanySalesRow,
} from "../../types/companySalesReport.type";

export const COMPANY_SALES_PATH = "/report/company-sales";
export const COMPANY_SALES_MENU_TITLE = "ยอดขายทั้งบริษัท";

/** The server's cap: date_from..date_to counted inclusively. */
export const MAX_SPAN_DAYS = 366;
export const ALL_BRANCHES = "all";

const TH_MONTH_LONG = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];
const TH_MONTH_SHORT = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
];

const DAY_MS = 24 * 60 * 60 * 1000;

const utcDay = (date: string): number => {
  const [year, month, day] = date.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};

/** Inclusive day count of `from`..`to` (`YYYY-MM-DD`); 0 when reversed. */
export const spanDays = (from: string, to: string): number =>
  from > to ? 0 : Math.round((utcDay(to) - utcDay(from)) / DAY_MS) + 1;

/** The reason a range cannot be asked for, or null. */
export const rangeErrorOf = (from: string, to: string): string | null => {
  if (!from || !to) return "กรุณาเลือกวันที่ให้ครบ";
  if (from > to) return "วันที่เริ่มต้องไม่หลังวันที่สิ้นสุด";
  if (spanDays(from, to) > MAX_SPAN_DAYS) return `เลือกช่วงวันที่ได้ไม่เกิน ${MAX_SPAN_DAYS} วัน`;
  return null;
};

/** The query: a branch only when one is picked. */
export const reportParams = (filters: {
  dateFrom: string;
  dateTo: string;
  groupBy: CompanySalesGroupBy;
  companyId: string;
}): CompanySalesReportParams => ({
  date_from: filters.dateFrom,
  date_to: filters.dateTo,
  group_by: filters.groupBy,
  ...(filters.companyId && filters.companyId !== ALL_BRANCHES ? { company_id: filters.companyId } : {}),
});

/** `2026-09-01` → `กันยายน 2569` by month, `1 ก.ย. 2569` by day (Buddhist era). */
export const periodLabel = (period: string, groupBy: CompanySalesGroupBy): string => {
  const match = period.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return period;
  const year = Number(match[1]) + 543;
  const month = Number(match[2]) - 1;
  if (groupBy === "month") return `${TH_MONTH_LONG[month]} ${year}`;
  return `${Number(match[3])} ${TH_MONTH_SHORT[month]} ${year}`;
};

/** `08-BK กรุงเทพฯ`; a branch id with no branch record shows as `สาขา <id>`. */
export const branchLabel = (label: CompanySalesBranchLabel): string =>
  label.branch_code
    ? `${label.branch_code}${label.branch_name ? ` ${label.branch_name}` : ""}`
    : `สาขา ${label.company_id}`;

/** Rows grouped under their branch, in the server's order (branch code, then period). */
export const groupRowsByBranch = (
  rows: CompanySalesRow[],
): { company_id: string; rows: CompanySalesRow[] }[] => {
  const groups: { company_id: string; rows: CompanySalesRow[] }[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.company_id === row.company_id) last.rows.push(row);
    else groups.push({ company_id: row.company_id, rows: [row] });
  }
  return groups;
};
